import { motion } from 'motion/react';
import { Photo } from '../types';
import {
  imgStreet,
  imgPortrait,
  imgArchitecture,
  imgBlackWhite,
  imgNature,
} from '../imports/SetjaUppSiduEinsOgKeyiscreation-1241-313';

interface CategoryOverviewProps {
  onCategoryClick: (categoryId: string) => void;
  photos?: Photo[];
}

const categories = [
  {
    id: 'street',
    title: 'Street Photography',
    image: imgStreet,
  },
  {
    id: 'portrait',
    title: 'Portrait & People',
    image: imgPortrait,
  },
  {
    id: 'architecture',
    title: 'Architecture',
    image: imgArchitecture,
  },
  {
    id: 'blackwhite',
    title: 'Black & White',
    image: imgBlackWhite,
  },
  {
    id: 'nature',
    title: 'Nature & Landscape',
    image: imgNature,
  },
];

/**
 * Pick a deterministic cover image from a category's photos,
 * rotating every 3 days. Falls back to the static imported image.
 */
function getCoverImage(categoryId: string, fallback: string, photos: Photo[]): string {
  const pool = photos.filter((p) => p.category === categoryId && p.imageUrl);
  if (pool.length === 0) return fallback;
  // Epoch day ÷ 3 gives a cycle index that changes every 3 days
  const dayIndex = Math.floor(Date.now() / (1000 * 60 * 60 * 24 * 3));
  return pool[dayIndex % pool.length].imageUrl;
}

export default function CategoryOverview({ onCategoryClick, photos = [] }: CategoryOverviewProps) {
  return (
    <div className="min-h-screen bg-black pt-[110px] pb-24">
      {/* Header */}
      <motion.div
        className="text-center mb-10 px-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
      >
        <h1
          className="text-white text-[44px] leading-[66px] tracking-[0.5px]"
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
        >
          Collections
        </h1>
        <p
          className="text-white/50 text-sm tracking-[0.6px] font-light"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          Browse by category
        </p>
      </motion.div>

      {/* Category Grid — 2 columns, square tiles */}
      <div className="w-full px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[3px]">
          {categories.map((cat, i) => (
            <motion.button
              key={cat.id}
              onClick={() => onCategoryClick(cat.id)}
              className="relative aspect-square overflow-hidden group cursor-pointer"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.12 * i }}
            >
              {/* Image */}
              <img
                src={getCoverImage(cat.id, cat.image, photos)}
                alt={cat.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.03]"
              />

              {/* Gradient overlay — matches Figma */}
              <div
                className="absolute inset-0 bg-gradient-to-t from-[rgba(0,0,0,0.65)] via-[40%] via-[rgba(0,0,0,0.25)] to-[rgba(0,0,0,0.1)] shadow-[inset_0_0_60px_20px_rgba(0,0,0,0.5)]"
                style={{ boxShadow: 'inset 0 0 80px 30px rgba(0,0,0,0.55), inset 0 0 20px 8px rgba(0,0,0,0.3)' }}
              />

              {/* Category title — bottom left */}
              <div className="absolute bottom-[27px] left-4 right-4">
                <h2
                  className="text-white text-[22px] leading-[27.5px] tracking-[0.3px] text-left"
                  style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
                >
                  {cat.title}
                </h2>
              </div>

              {/* Gold hover line — bottom edge */}
              <div
                className="absolute bottom-0 left-0 h-[2px] bg-[#f4c430] w-0 group-hover:w-full transition-all duration-500 ease-out"
              />
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
}